import * as signalR from '@microsoft/signalr'

export type UserNotificationPayload = {
  notificationId: number
  organizationId: number
  notificationTypeId: number
  notificationType: string
  title: string
  messageHtml: string
  displayModeId: number
  displayMode: string
  startDateTimeUtc: string
  endDateTimeUtc: string | null
  logoutDeadlineUtc: string | null
  popupDisplaySeconds: number | null
  priority: number
  eventType: string
  remainingLogoutSeconds?: number | null
  status?: string
  audienceTypeId?: number
  inAppEnabled?: boolean
  popupEnabled?: boolean
  pushEnabled?: boolean
  forceLogoutEnabled?: boolean
  forceLogoutSeconds?: number | null
}

type Handler = (eventType: string, payload: UserNotificationPayload) => void

let connection: signalR.HubConnection | null = null
let handlers: Handler[] = []

const HUB_PATH = '/UserNotifications'
const EVENTS = [
  'NotificationStarted',
  'NotificationUpdated',
  'NotificationCancelled',
  'NotificationEnded',
  'MaintenanceLogoutWarning',
] as const

export const subscribeUserNotifications = (handler: Handler) => {
  handlers.push(handler)
  return () => {
    handlers = handlers.filter((h) => h !== handler)
  }
}

const notify = (eventType: string, payload: UserNotificationPayload) => {
  handlers.forEach((h) => {
    try {
      h(eventType, payload)
    } catch {
      /* ignore */
    }
  })
}

const normalize = (raw: any): UserNotificationPayload | null => {
  const notificationId = Number(raw?.notificationId ?? raw?.NotificationId ?? 0)
  if (!notificationId) return null
  return {
    notificationId,
    organizationId: Number(raw?.organizationId ?? raw?.OrganizationId ?? 0),
    notificationTypeId: Number(raw?.notificationTypeId ?? raw?.NotificationTypeId ?? 0),
    notificationType: String(raw?.notificationType ?? raw?.NotificationType ?? ''),
    title: String(raw?.title ?? raw?.Title ?? ''),
    messageHtml: String(raw?.messageHtml ?? raw?.MessageHtml ?? ''),
    displayModeId: Number(raw?.displayModeId ?? raw?.DisplayModeId ?? 1),
    displayMode: String(raw?.displayMode ?? raw?.DisplayMode ?? ''),
    startDateTimeUtc: String(raw?.startDateTimeUtc ?? raw?.StartDateTimeUtc ?? ''),
    endDateTimeUtc: raw?.endDateTimeUtc ?? raw?.EndDateTimeUtc ?? null,
    logoutDeadlineUtc: raw?.logoutDeadlineUtc ?? raw?.LogoutDeadlineUtc ?? null,
    popupDisplaySeconds: raw?.popupDisplaySeconds ?? raw?.PopupDisplaySeconds ?? null,
    priority: Number(raw?.priority ?? raw?.Priority ?? 99),
    eventType: String(raw?.eventType ?? raw?.EventType ?? ''),
    remainingLogoutSeconds: raw?.remainingLogoutSeconds ?? raw?.RemainingLogoutSeconds ?? null,
    status: raw?.status ?? raw?.Status,
    audienceTypeId: Number(raw?.audienceTypeId ?? raw?.AudienceTypeId ?? 1),
    inAppEnabled: raw?.inAppEnabled ?? raw?.InAppEnabled,
    popupEnabled: raw?.popupEnabled ?? raw?.PopupEnabled,
    pushEnabled: raw?.pushEnabled ?? raw?.PushEnabled,
    forceLogoutEnabled: raw?.forceLogoutEnabled ?? raw?.ForceLogoutEnabled,
    forceLogoutSeconds: raw?.forceLogoutSeconds ?? raw?.ForceLogoutSeconds ?? null,
  }
}

const buildHubUrl = (baseURL: string, orgId?: number | null, orgUserId?: number | null) => {
  const params = new URLSearchParams()
  if (orgId) params.set('orgId', String(orgId))
  if (orgUserId) {
    params.set('orgUserId', String(orgUserId))
    params.set('userAccountId', String(orgUserId))
  }
  const qs = params.toString()
  const root = `${baseURL.replace(/\/$/, '')}${HUB_PATH}`
  return qs ? `${root}?${qs}` : root
}

export const getUserNotificationConnectionId = () => connection?.connectionId ?? undefined

export const startUserNotificationsHub = async (opts: {
  baseURL?: string | null
  token?: string | null
  orgId?: number | null
  orgUserId?: number | null
}) => {
  const { baseURL, token, orgId, orgUserId } = opts
  if (!baseURL || !token) return null

  if (connection) {
    if (connection.state === signalR.HubConnectionState.Connected) return connection
    try {
      await connection.stop()
    } catch {
      /* ignore */
    }
    connection = null
  }

  connection = new signalR.HubConnectionBuilder()
    .withUrl(buildHubUrl(baseURL, orgId, orgUserId), {
      accessTokenFactory: () => token,
      transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Warning)
    .build()

  EVENTS.forEach((eventName) => {
    connection!.on(eventName, (payload: any) => {
      const normalized = normalize(payload)
      if (normalized) notify(eventName, { ...normalized, eventType: eventName })
    })
  })

  connection.onreconnected(() => {
    notify('__reconnected__', {
      notificationId: 0,
      organizationId: orgId || 0,
      notificationTypeId: 0,
      notificationType: '',
      title: '',
      messageHtml: '',
      displayModeId: 0,
      displayMode: '',
      startDateTimeUtc: '',
      endDateTimeUtc: null,
      logoutDeadlineUtc: null,
      popupDisplaySeconds: null,
      priority: 0,
      eventType: '__reconnected__',
    })
  })

  try {
    await connection.start()
  } catch (err) {
    console.warn('UserNotifications hub connect failed', err)
    connection = null
    return null
  }

  return connection
}

export const stopUserNotificationsHub = async () => {
  if (!connection) return
  try {
    await connection.stop()
  } catch {
    /* ignore */
  }
  connection = null
}
